import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfigStateService } from '@abp/ng.core';
import { PROFILE_PICTURE } from '@volo/abp.commercial.ng.ui/config';
import {
  Observable,
  Subject,
  catchError,
  filter,
  of,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs';
import { environment } from 'src/environments/environment';

/** Upper bound for cached entries; the oldest keys are evicted first. */
const PICTURE_CACHE_MAX_SIZE = 100;

/**
 * Resolves agent avatars from the backend's 64x64 thumbnail endpoint and keeps them in memory for the
 * session.
 *
 * The thumbnail is produced when the user saves their picture (see the AuthServer's account app service
 * override) and served by `GET api/agentic-cx/agent/{id}/photo-thumbnail`, which reads it from Redis and
 * falls back to MinIO. A few KB per avatar, so a list of agents can fetch one per row — as opposed to the
 * full-resolution picture ABP's own `/api/account/profile-picture/{id}` returns as base64 JSON.
 *
 * A user without a picture — and any failing request — resolves to `null` so callers fall back to initials.
 * That `null` is cached too: most users have no picture, and re-asking on every render would put the whole
 * list back on the wire.
 */
@Injectable({ providedIn: 'root' })
export class ProfilePictureCacheService {
  private readonly http = inject(HttpClient);
  private readonly configState = inject(ConfigStateService);
  private readonly currentUserPicture$ = inject(PROFILE_PICTURE);

  private readonly baseUrl = environment.apis.default.url.replace(/\/$/, '');
  private readonly cache = new Map<string, Observable<string | null>>();
  /** Emits the (lowercased) id of a user whose cached avatar has just been dropped. */
  private readonly invalidations = new Subject<string>();

  constructor() {
    // The manage-profile page publishes the freshly saved picture here; dropping the stale entry makes every
    // avatar of the current user pick up the new thumbnail when it is next rendered.
    this.currentUserPicture$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.invalidate(this.currentUserId));
  }

  /**
   * Emits an `<img src>` for the user's avatar once, or `null` when they have no picture.
   * Repeated calls for the same user share a single request.
   *
   * Avatars that stay on screen should prefer {@link watchPicture}, which also reacts to invalidation.
   */
  getPicture(userId: string | null | undefined): Observable<string | null> {
    if (!userId) {
      return of(null);
    }

    const key = userId.toLowerCase();
    let picture$ = this.cache.get(key);

    if (!picture$) {
      picture$ = this.http
        .get(`${this.baseUrl}/api/agentic-cx/agent/${userId}/photo-thumbnail`, {
          responseType: 'blob',
          // A missing thumbnail is the normal case, not something to show the user a toast about.
          headers: { 'hide-errors': 'true' },
        })
        .pipe(
          switchMap(blob => (blob && blob.size > 0 ? this.toDataUrl(blob) : of(null))),
          catchError(() => of(null)),
          shareReplay({ bufferSize: 1, refCount: false }),
        );

      this.cache.set(key, picture$);
      this.trimCache();
    }

    return picture$;
  }

  /**
   * Like {@link getPicture}, but keeps emitting: an avatar subscribed to this stream refetches on its
   * own as soon as the user's picture is invalidated, instead of waiting to be rendered again.
   */
  watchPicture(userId: string | null | undefined): Observable<string | null> {
    if (!userId) {
      return of(null);
    }

    const key = userId.toLowerCase();

    return this.invalidations.pipe(
      filter(invalidated => invalidated === key),
      startWith(null),
      switchMap(() => this.getPicture(userId)),
    );
  }

  /** Forgets the cached avatar of a user and makes the avatars showing it refetch. */
  invalidate(userId: string | null | undefined): void {
    if (!userId) {
      return;
    }

    const key = userId.toLowerCase();
    this.cache.delete(key);
    this.invalidations.next(key);
  }

  private get currentUserId(): string | null {
    return this.configState.getDeep('currentUser.id') ?? null;
  }

  /**
   * Reads the blob as a data URL, so what is cached and bound to `<img src>` is a plain string — no object
   * URL to revoke when an entry is evicted.
   */
  private toDataUrl(blob: Blob): Observable<string | null> {
    return new Observable<string | null>(subscriber => {
      const reader = new FileReader();

      reader.onload = () => {
        subscriber.next(typeof reader.result === 'string' ? reader.result : null);
        subscriber.complete();
      };

      reader.onerror = () => {
        subscriber.next(null);
        subscriber.complete();
      };

      reader.readAsDataURL(blob);
    });
  }

  private trimCache(): void {
    const overflow = this.cache.size - PICTURE_CACHE_MAX_SIZE;
    if (overflow <= 0) {
      return;
    }

    const keys = this.cache.keys();
    for (let i = 0; i < overflow; i++) {
      this.cache.delete(keys.next().value!);
    }
  }
}
